import { type Channel as EventTeamChannel, type EventTeam } from '@domain/eventTeam';

/** Flattens the team/channel tree to the channel rows (team channels + channels without a team). */
export const collectTreeChannels = (tree: EventTeam): EventTeamChannel[] => [
  ...tree.teams.flatMap((team) => team.channels),
  ...tree.channelsWithoutTeams,
];

export const collectChannelIds = (tree: EventTeam): string[] =>
  collectTreeChannels(tree).map((channel) => channel.channel.id);

/** Channel ids whose sales are closed at the event level — never offered, regardless of selection. */
export const collectClosedChannelIds = (tree: EventTeam): Set<string> =>
  new Set(
    collectTreeChannels(tree)
      .filter((channel) => channel.isTicketSalesClosed)
      .map((channel) => channel.channel.id)
  );

export const collectChannelCapById = (tree: EventTeam): Map<string, number | null> =>
  new Map(collectTreeChannels(tree).map((channel) => [channel.channel.id, channel.maxTicketQuantity]));

/**
 * Ids of teams with some but not all selectable channels selected — the per-channel cap UI expands those
 * by default. Sales-closed channels are excluded: the multi-select disables them, so counting them would
 * keep a team "partially selected" forever even with every selectable channel picked.
 */
export const collectPartiallySelectedTeamIds = (tree: EventTeam, selectedChannelIds: string[]): string[] => {
  const selected = new Set(selectedChannelIds);
  return tree.teams
    .filter((team) => {
      const selectable = team.channels.filter((channel) => !channel.isTicketSalesClosed);
      const selectedCount = selectable.filter((channel) => selected.has(channel.channel.id)).length;
      return selectedCount > 0 && selectedCount < selectable.length;
    })
    .map((team) => team.id);
};

/** Narrows the tree to the selected channels (drops empty teams) — for the per-channel cap UI. */
export const filterTreeToSelected = (tree: EventTeam, selectedChannelIds: string[]): EventTeam => {
  const selected = new Set(selectedChannelIds);
  return {
    teams: tree.teams
      .map((team) => ({ ...team, channels: team.channels.filter((channel) => selected.has(channel.channel.id)) }))
      .filter((team) => team.channels.length > 0),
    channelsWithoutTeams: tree.channelsWithoutTeams.filter((channel) => selected.has(channel.channel.id)),
  };
};

/** Writes the caps edited on a filtered subtree back onto the full tree (by channel/team id). */
export const mergeTreeCaps = (fullTree: EventTeam, edited: EventTeam): EventTeam => {
  const channelCap = new Map<string, number | null>();
  const teamCap = new Map<string, number | null>();
  edited.teams.forEach((team) => {
    teamCap.set(team.id, team.maxTicketQuantity);
    team.channels.forEach((channel) => channelCap.set(channel.channel.id, channel.maxTicketQuantity));
  });
  edited.channelsWithoutTeams.forEach((channel) => channelCap.set(channel.channel.id, channel.maxTicketQuantity));

  const applyChannel = (channel: EventTeamChannel): EventTeamChannel =>
    channelCap.has(channel.channel.id)
      ? { ...channel, maxTicketQuantity: channelCap.get(channel.channel.id) ?? null }
      : channel;

  return {
    teams: fullTree.teams.map((team) => ({
      ...team,
      maxTicketQuantity: teamCap.has(team.id) ? teamCap.get(team.id) ?? null : team.maxTicketQuantity,
      channels: team.channels.map(applyChannel),
    })),
    channelsWithoutTeams: fullTree.channelsWithoutTeams.map(applyChannel),
  };
};
